import { useEffect, useRef } from "react";
import {
  sendNativeNotification,
  setNotificationClickHandler,
} from "@/lib/notifications";
import { playSound } from "@/lib/sounds";
import { useNotificationFilter } from "@/hooks/use-notification-filter";
import { useMessageInboxItems } from "@/lib/selectors";
import { addSentryBreadcrumb } from "@/lib/sentry";

interface OpenChannelOpts {
  workspaceId?: string | null;
  targetMessageId?: string;
  targetThreadId?: string;
}

/**
 * App-level hook that owns all desktop OS-banner + sound notification logic:
 * - Derives newly-arrived items from `messages.inboxForUser` (via
 *   `useMessageInboxItems`) — the SAME source the inbox LIST and the
 *   inbox badge read, so banners / sounds / badge / list all agree.
 * - Filters via per-event workspace preferences.
 * - Plays sounds, dispatches native notifications.
 * - Registers click handler for navigation.
 *
 * Why not `notification_events`? Migration 104 row-filtered the
 * `notification_events` Zero publication to admin reasons only
 * (role_upgraded / added_to_space / added_to_channel). DM / mention /
 * notify=all rows never replicate to the desktop client, so reading
 * banners off that query made the loop iterate an empty set and fired
 * ZERO banners/sounds for messages. Deriving from `messages` (matching
 * mig 104's intent of "the inbox query owns message-backed items") is
 * the fix — no publication / migration change required.
 */
export function useNotifications({
  userId,
  selectedChannel,
  mutedSet,
  openChannelWithContext,
}: {
  userId: string | undefined;
  selectedChannel: string | null;
  mutedSet: Set<string>;
  openChannelWithContext: (channelId: string, opts?: OpenChannelOpts) => void;
}) {
  const { shouldShowNotification } = useNotificationFilter(userId);

  // Message-derived inbox items (DMs / @mentions / notify=all channel
  // messages), already unread-filtered against `last_read_at` and
  // excluding self-authored / system / call kinds. Same source as the
  // inbox feed + badge.
  const inboxItems = useMessageInboxItems();

  // Register notification click handler to navigate to channel
  useEffect(() => {
    setNotificationClickHandler(
      (channelId, messageId, workspaceId, threadId) => {
        openChannelWithContext(channelId, {
          targetMessageId: messageId,
          targetThreadId: threadId,
          workspaceId,
        });
      },
    );
  }, [openChannelWithContext]);

  // Tracks message ids we've already accounted for. `null` until the
  // first run for a given user, so a fresh mount / reconnect seeds the
  // current set WITHOUT firing — only genuinely new arrivals banner.
  const notifiedMessageIds = useRef<Set<string> | null>(null);
  const prevUserIdRef = useRef<string | undefined>(undefined);
  const sentMountedBreadcrumbRef = useRef(false);

  useEffect(() => {
    if (!userId) return;
    // One-time breadcrumb so a Sentry event from prod tells us whether
    // the notifications hook even mounted (and how many items were
    // available at mount). Investigates "no banners/sounds in prod".
    if (!sentMountedBreadcrumbRef.current) {
      sentMountedBreadcrumbRef.current = true;
      addSentryBreadcrumb({
        category: "notif",
        message: "useNotifications mounted",
        data: {
          userId,
          itemsAtMount: inboxItems.length,
          notificationPermission:
            typeof Notification !== "undefined"
              ? Notification.permission
              : "unavailable",
        },
      });
    }

    // Reset tracked IDs when user changes (logout/login or account switch)
    if (prevUserIdRef.current !== userId) {
      notifiedMessageIds.current = null;
      prevUserIdRef.current = userId;
    }

    // First run for this user: seed known ids and return WITHOUT firing,
    // so a fresh mount / reconnect does not banner every existing item.
    if (notifiedMessageIds.current === null) {
      notifiedMessageIds.current = new Set(inboxItems.map((it) => it.id));
      return;
    }

    const toNotify: typeof inboxItems = [];
    for (const item of inboxItems) {
      // Already accounted for — seed or a prior tick.
      if (notifiedMessageIds.current.has(item.id)) continue;
      notifiedMessageIds.current.add(item.id);

      // Never notify a user of their own message. `useMessageInboxItems`
      // already drops self-authored rows, but keep the guard so this
      // hook can't regress independently.
      if (item.actorUserId === userId) continue;

      // Skip notification if we're focused on this channel.
      if (item.channelId === selectedChannel && document.hasFocus()) {
        continue;
      }

      // Muted channel: suppress BOTH banner and sound for anything that
      // isn't a direct @mention. The old `notification_events` producer
      // skipped a muted non-mention entirely; reading off `inboxForUser`
      // (which has no `cm.muted` filter) lost that — a notify=all + muted
      // channel would pop an OS banner with only the sound suppressed.
      // A muted DM (reason "dm") is correctly suppressed here too; only a
      // direct mention pierces the mute.
      if (mutedSet.has(item.channelId) && item.reason !== "mention") continue;

      // Apply notification preferences filter (per-event workspace).
      if (
        !shouldShowNotification({
          id: item.id,
          channel_id: item.channelId,
          workspace_id: item.workspaceId ?? null,
          reason: item.reason,
          body: item.body,
        })
      ) {
        continue;
      }

      toNotify.push(item);
    }

    if (toNotify.length === 0) return;

    addSentryBreadcrumb({
      category: "notif",
      message: "firing notifications",
      data: {
        toNotifyCount: toNotify.length,
        notificationPermission:
          typeof Notification !== "undefined"
            ? Notification.permission
            : "unavailable",
        hasElectronApi:
          typeof window !== "undefined" &&
          !!(window as unknown as { __electronApi?: unknown }).__electronApi,
      },
    });

    // Only play a sound if at least one item is from a non-muted channel.
    const unmutedItems = toNotify.filter((it) => !mutedSet.has(it.channelId));
    if (unmutedItems.length > 0) {
      const hasMention = unmutedItems.some((it) => it.reason === "mention");
      playSound(hasMention ? "mention" : "notification");
    }

    const MAX_INDIVIDUAL = 3;
    if (toNotify.length > MAX_INDIVIDUAL) {
      sendNativeNotification({
        title: "Ano",
        body: `${toNotify.length} new messages`,
      });
    } else {
      for (const item of toNotify) {
        sendNativeNotification({
          title: item.title,
          body: item.body,
          channelId: item.channelId,
          messageId: item.messageId,
          threadId: item.threadId,
          workspaceId: item.workspaceId,
        });
      }
    }
  }, [inboxItems, userId, selectedChannel, mutedSet, shouldShowNotification]);
}
